import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase-config';
import { FiArrowLeft, FiUpload, FiCheck, FiClock, FiTruck, FiDollarSign, FiAlertCircle, FiDownload } from 'react-icons/fi';
import { generateReceipt } from '../utils/receiptGenerator';

const STATUS_ORDER = ['AWAITING_FUNDS', 'FUNDS_RECEIVED', 'PROCESSING', 'SENT', 'COMPLETED'];

const STATUS_META = {
  AWAITING_FUNDS: { label: 'Awaiting Payment', icon: <FiClock />, color: 'text-status-yellow', bg: 'bg-status-yellow', bgLight: 'bg-status-yellow-bg' },
  FUNDS_RECEIVED: { label: 'Payment Confirmed', icon: <FiDollarSign />, color: 'text-status-blue', bg: 'bg-status-blue', bgLight: 'bg-status-blue-bg' },
  PROCESSING:     { label: 'Processing Transfer', icon: <FiClock />, color: 'text-status-purple', bg: 'bg-status-purple', bgLight: 'bg-status-purple-bg' },
  SENT:           { label: 'Transfer in Progress', icon: <FiTruck />, color: 'text-status-blue', bg: 'bg-status-blue', bgLight: 'bg-status-blue-bg' },
  COMPLETED:      { label: 'Completed', icon: <FiCheck />, color: 'text-status-green', bg: 'bg-status-green', bgLight: 'bg-status-green-bg' },
  FAILED:         { label: 'Transaction Failed', icon: <FiAlertCircle />, color: 'text-status-red', bg: 'bg-status-red', bgLight: 'bg-status-red-bg' },
};

const formatCurrency = (amount, currency) => {
  if (amount == null) return '—';
  const n = typeof amount === 'number' ? amount : parseFloat(amount);
  if (isNaN(n)) return '—';
  const fmt = n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (currency === 'NGN') return '₦' + fmt;
  if (currency === 'CFA' || currency === 'XOF') return 'CFA ' + fmt;
  return fmt;
};

const formatDate = (d) => {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function TransactionDetail({ merchantData }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const fetchTransaction = useCallback(async () => {
    try {
      setLoading(true);
      const fns = getFunctions();
      const merchantApi = httpsCallable(fns, 'merchantApi');
      const result = await merchantApi({ action: 'getMerchantTransaction', transactionId: id });
      setTx(result.data.transaction);
    } catch (err) {
      console.error('Failed to load transaction:', err);
      setError('Transaction not found');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchTransaction(); }, [fetchTransaction]);

  const handleUploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Max 10MB.');
      return;
    }
    try {
      setUploading(true);
      setError('');
      const ext = file.name.split('.').pop();
      const path = `proof_of_payment/${id}_${Date.now()}.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const fns = getFunctions();
      const merchantApi = httpsCallable(fns, 'merchantApi');
      await merchantApi({ action: 'uploadProofOfPayment', transactionId: id, proofOfPayment: url });
      await fetchTransaction();
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Failed to upload proof of payment');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tx) {
    return (
      <div className="text-center py-16">
        <p className="text-text-secondary mb-4">{error || 'Transaction not found'}</p>
        <button onClick={() => navigate('/transactions')} className="text-primary text-sm font-medium hover:underline">
          Back to transactions
        </button>
      </div>
    );
  }

  const isFailed = tx.status === 'FAILED';
  const currentStepIndex = STATUS_ORDER.indexOf(tx.status);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/transactions')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <FiArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-text-main">Transaction Details</h1>
          <p className="text-xs text-text-secondary font-mono">{tx.reference}</p>
        </div>
      </div>

      {/* Status Tracker */}
      <div className="bg-surface rounded-xl shadow-card p-5 sm:p-6">
        <h2 className="text-sm font-semibold text-text-main mb-5">Status</h2>

        {isFailed ? (
          <div className="flex items-center gap-3 p-4 bg-status-red-bg rounded-lg">
            <FiAlertCircle className="text-status-red" size={24} />
            <div>
              <p className="text-sm font-semibold text-status-red">Transaction could not be completed</p>
              {tx.adminNotes && <p className="text-xs text-text-secondary mt-1">{tx.adminNotes}</p>}
            </div>
          </div>
        ) : (
          <>
            {/* Desktop: horizontal tracker */}
            <div className="hidden sm:flex items-center justify-between">
              {STATUS_ORDER.map((status, i) => {
                const meta = STATUS_META[status];
                const isCompleted = i < currentStepIndex;
                const isCurrent = i === currentStepIndex;
                const isUpcoming = i > currentStepIndex;
                return (
                  <React.Fragment key={status}>
                    <div className="flex flex-col items-center gap-1.5">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm ${
                        isCompleted ? 'bg-status-green text-white' :
                        isCurrent ? `${meta.bgLight} ${meta.color}` :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        {isCompleted ? <FiCheck size={16} /> : meta.icon}
                      </div>
                      <span className={`text-[10px] font-medium text-center leading-tight max-w-[60px] ${
                        isCurrent ? meta.color : isUpcoming ? 'text-gray-400' : 'text-status-green'
                      }`}>
                        {meta.label}
                      </span>
                    </div>
                    {i < STATUS_ORDER.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-1 ${isCompleted ? 'bg-status-green' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Mobile: vertical tracker */}
            <div className="flex sm:hidden flex-col gap-0">
              {STATUS_ORDER.map((status, i) => {
                const meta = STATUS_META[status];
                const isCompleted = i < currentStepIndex;
                const isCurrent = i === currentStepIndex;
                const isUpcoming = i > currentStepIndex;
                return (
                  <React.Fragment key={status}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-sm ${
                        isCompleted ? 'bg-status-green text-white' :
                        isCurrent ? `${meta.bgLight} ${meta.color}` :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        {isCompleted ? <FiCheck size={14} /> : meta.icon}
                      </div>
                      <span className={`text-xs font-medium ${
                        isCurrent ? meta.color : isUpcoming ? 'text-gray-400' : 'text-status-green'
                      }`}>
                        {meta.label}
                      </span>
                    </div>
                    {i < STATUS_ORDER.length - 1 && (
                      <div className={`w-0.5 h-5 ml-[15px] ${isCompleted ? 'bg-status-green' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Estimated time — only when in progress */}
      {!isFailed && tx.status !== 'COMPLETED' && (
        <div className="bg-primary/5 border border-primary/10 rounded-xl px-4 py-3 flex items-center gap-3">
          <FiClock className="text-primary flex-shrink-0" size={18} />
          <p className="text-xs text-primary font-medium leading-relaxed">
            Your transaction is being processed and will typically complete within <strong>0 – 6 hours</strong>. You'll receive a notification at each step.
          </p>
        </div>
      )}

      {/* Transaction Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-surface rounded-xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-text-main mb-3">Exchange Details</h2>
          <div className="space-y-2.5">
            <DetailRow label="You Send" value={formatCurrency(tx.amount, tx.inputCurrency)} />
            <DetailRow label="You Receive" value={formatCurrency(tx.outputAmount, tx.outputCurrency)} />
            <DetailRow label="Exchange Rate" value={tx.rate ? `1 ${tx.inputCurrency} = ${tx.rate} ${tx.outputCurrency}` : '—'} />
            <DetailRow label="Direction" value={tx.settlementDirection === 'NGN_FIRST' ? 'NGN First' : 'CFA First'} />
            <DetailRow label="Created" value={formatDate(tx.createdAt)} />
            {tx.completedAt && <DetailRow label="Completed" value={formatDate(tx.completedAt)} />}
          </div>
        </div>

        <div className="bg-surface rounded-xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-text-main mb-3">Recipient</h2>
          <div className="space-y-2.5">
            {tx.payoutMethod && <DetailRow label="Payout Method" value={tx.payoutMethod === 'MOBILE_MONEY' ? 'Mobile Money' : tx.payoutMethod === 'CASH_PICKUP' ? 'Cash Pickup' : 'Bank Transfer'} />}
            {tx.recipientName && <DetailRow label="Name" value={tx.recipientName} />}
            {tx.recipientBank && <DetailRow label={tx.payoutMethod === 'MOBILE_MONEY' ? 'Network' : 'Bank'} value={tx.recipientBank} />}
            {tx.recipientAccount && <DetailRow label="Account" value={tx.recipientAccount} />}
            {tx.recipientPhone && <DetailRow label="Phone" value={tx.recipientPhone} />}
            {tx.pickupCode && <DetailRow label="Pickup Code" value={tx.pickupCode} />}
            {tx.pickupLocation && <DetailRow label="Location" value={tx.pickupLocation} />}
          </div>
        </div>
      </div>

      {/* Download Receipt */}
      <div className="bg-surface rounded-xl shadow-card p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-text-main">Transaction Receipt</h2>
            <p className="text-xs text-text-secondary mt-0.5">Download a receipt with the current status</p>
          </div>
          <button
            onClick={() => generateReceipt(tx)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-light transition-colors"
          >
            <FiDownload size={16} />
            Download
          </button>
        </div>
      </div>

      {/* Proof of Payment */}
      <div className="bg-surface rounded-xl shadow-card p-5">
        <h2 className="text-sm font-semibold text-text-main mb-3">Proof of Payment</h2>
        {tx.proofOfPayment ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <FiCheck className="text-status-green" size={14} />
              <span className="text-xs text-status-green font-medium">Uploaded</span>
            </div>
            <img
              src={tx.proofOfPayment}
              alt="Proof of payment"
              className="max-w-full max-h-64 rounded-lg border border-gray-200 object-contain"
            />
          </div>
        ) : tx.status === 'AWAITING_FUNDS' ? (
          <div>
            <p className="text-xs text-text-secondary mb-3">
              Upload your transfer receipt to speed up payment confirmation.
            </p>
            <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium cursor-pointer hover:bg-gray-50 transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <FiUpload size={16} />
              {uploading ? 'Uploading...' : 'Upload Proof'}
              <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleUploadProof} disabled={uploading} />
            </label>
          </div>
        ) : (
          <p className="text-xs text-text-secondary">No proof uploaded yet</p>
        )}
        {error && <p className="text-xs text-status-red mt-2">{error}</p>}
      </div>

      {/* Transaction Notes */}
      {tx.adminNotes && (
        <div className="bg-surface rounded-xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-text-main mb-2">Transaction Notes</h2>
          <p className="text-sm text-text-secondary">{tx.adminNotes}</p>
        </div>
      )}

      {/* Activity Log */}
      {tx.statusHistory && tx.statusHistory.length > 0 && (
        <div className="bg-surface rounded-xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-text-main mb-3">Activity</h2>
          <div className="space-y-3">
            {tx.statusHistory.map((entry, i) => {
              const meta = STATUS_META[entry.status] || {};
              const userNote = {
                AWAITING_FUNDS: 'Transaction created',
                FUNDS_RECEIVED: 'Payment has been confirmed',
                PROCESSING: 'Transfer is being processed',
                SENT: 'Transfer has been sent',
                COMPLETED: 'Transaction completed successfully',
                FAILED: 'Transaction could not be completed',
              }[entry.status] || meta.label || entry.status;
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs mt-0.5 ${meta.bgLight || 'bg-gray-100'} ${meta.color || 'text-gray-500'}`}>
                    {meta.icon || <FiClock size={12} />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-main">{meta.label || entry.status}</p>
                    <p className="text-xs text-text-secondary">{userNote}</p>
                    <p className="text-[10px] text-text-secondary mt-0.5">{formatDate(entry.timestamp)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Trust footer */}
      <div className="text-center py-2">
        <p className="text-[10px] text-text-secondary">Processed securely by KwikX</p>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-text-secondary">{label}</span>
      <span className="text-sm font-medium text-text-main text-right">{value || '—'}</span>
    </div>
  );
}
